import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { ApiCallingService } from "../../services/api-calling.service";
import { ConfigApi } from "../../config-api";
import { ToastrService } from "ngx-toastr";
import { Globals } from "../../common/global";
import { HttpParams } from "@angular/common/http";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { VendingOrderModalComponent } from "../vending-order-modal/vending-order-modal.component";

@Component({
  selector: "app-vending-track-order",
  templateUrl: "./vending-track-order.component.html",
  styleUrls: ["./vending-track-order.component.css"],
})
export class VendingTrackOrderComponent implements OnInit {
  orders: any[] = [];
  page: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  currency: string = localStorage.getItem("CUR") || "SEK";
  loading: boolean = false;
  searchQuery: string = "";
  private searchDebounceTimer: any;
  statusList = [
    "all",
    "pending",
    "accepted",
    "completed",
    "cancelled",
    "outForDelivery",
  ];
  selectedStatus = "all";
  statusDebounceTimer: any;

  @ViewChild("userFilterContainer") userFilterContainer!: ElementRef;

  userSearchQuery: string = "";
  users: any[] = [];
  selectedUserId: string = "";
  selectedUserName: string = "";
  isUserDropdownOpen: boolean = false;
  userSearchDebounceTimer: any;
  loadingUsers: boolean = false;
  exportingCsv: boolean = false;

  constructor(
    private router: Router,
    private apiCallingService: ApiCallingService,
    private toastr: ToastrService,
    public globals: Globals,
    private modalService: NgbModal,
    private eRef: ElementRef,
  ) {}

  @HostListener("document:click", ["$event"])
  clickout(event: any) {
    if (
      this.userFilterContainer &&
      !this.userFilterContainer.nativeElement.contains(event.target)
    ) {
      this.isUserDropdownOpen = false;
    }
  }

  ngOnInit(): void {
    if (!this.globals.user) {
      this.globals.getUser();
    }
    this.getOrders();
    this.searchUsers(false);
  }

  getOrders(): void {
    this.loading = true;
    const user = this.globals.user || this.globals.getUser();
    const user_id = user ? user._id : "";
    const pageIndex = this.page > 0 ? this.page - 1 : 0;

    let params = new HttpParams()
      .set("operator_id", user_id)
      .set("page", pageIndex.toString())
      .set("limit", this.pageSize.toString());

    if (this.searchQuery) {
      params = params.set("search", this.searchQuery);
    }

    if (this.selectedStatus && this.selectedStatus !== "all") {
      params = params.set("status", this.selectedStatus);
    }

    if (this.selectedUserId) {
      params = params.set("user_id", this.selectedUserId);
    }

    this.apiCallingService
      .callAPI(
        {},
        "GET",
        `${ConfigApi.URLS.VENDING.ORDER_LIST}?${params.toString()}`,
      )
      .subscribe(
        (res) => {
          this.loading = false;
          if (this.apiCallingService.handleSuccess(res)) {
            this.orders = res.data;
            this.totalCount = res.total;
          }
        },
        (error) => {
          this.loading = false;
          this.toastr.error("Failed to load orders");
        },
      );
  }

  loadPage(page: number): void {
    if (page) {
      this.page = page;
      this.getOrders();
    }
  }

  viewOrderDetails(order: any): void {
    this.router.navigate(["/user/vending-orders", order.orderNumber]);
  }

  confirmAction(
    order: any,
    actionType: string,
    requiresReason: boolean = false,
  ): void {
    const modalRef = this.modalService.open(VendingOrderModalComponent, {
      centered: true,
      backdrop: "static",
    });
    const title =
      actionType.toLowerCase() === "ready for pickup"
        ? "Ready for Pickup"
        : actionType.toLowerCase() === "out for delivery"
          ? "Out for delivery Order"
          : `${actionType.charAt(0).toUpperCase()}${actionType.slice(1)} Order`;
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.modalType = "status";
    modalRef.componentInstance.order = order;
    modalRef.componentInstance.actionType = actionType;
    modalRef.componentInstance.requiresReason = requiresReason;
    modalRef.componentInstance.setOutForDeliveryDefaults?.();

    modalRef.result
      .then((result) => {
        if (result) {
          if (actionType.toLowerCase() === "ready for pickup") {
            this.performReadyForPickUp(order._id);
          } else if (actionType.toLowerCase() === "out for delivery") {
            this.performOutForDelivery(order._id, result);
          } else {
            this.performAction(order._id, actionType, result);
          }
        }
      })
      .catch(() => {});
  }

  performReadyForPickUp(orderId: string): void {
    this.apiCallingService
      .callAPI(
        {},
        "POST",
        `${ConfigApi.URLS.VENDING.READY_FOR_PICKUP}/${orderId}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success("Order marked as ready for pickup");
            this.getOrders();
          }
        },
        () => {
          this.toastr.error("Failed to mark order as ready for pickup");
        },
      );
  }

  performOutForDelivery(
    orderId: string,
    payload: { riderMobile: string; message: string },
  ): void {
    this.apiCallingService
      .callAPI(
        { riderMobile: payload.riderMobile, message: payload.message },
        "POST",
        `${ConfigApi.URLS.VENDING.OUT_FOR_DELIVERY}/${orderId}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success("Order marked as out for delivery");
            this.getOrders();
          }
        },
        () => {
          this.toastr.error("Failed to mark order as out for delivery");
        },
      );
  }

  performAction(orderId: string, actionType: string, reason?: any): void {
    const statusMap: { [key: string]: string } = {
      accept: "accepted",
      "out for delivery": "outForDelivery",
      complete: "completed",
      cancel: "cancelled",
    };

    const status = statusMap[actionType.toLowerCase()];
    if (!status) return;

    let payload: any = { status };

    if (actionType.toLowerCase() === "cancel" && reason) {
      payload.cancel_reason = reason;
    } else if (reason && typeof reason === "object") {
      payload = { ...payload, ...reason };
    }

    const headers = this.apiCallingService
      .getHeader()
      .set("Content-Type", "application/json");

    this.apiCallingService
      .customAPI(
        headers,
        payload,
        "POST",
        `${ConfigApi.URLS.VENDING.UPDATE_ORDER}/${orderId}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            const actionMessage = actionType.toLowerCase() + "ed";
            this.toastr.success(`Order ${actionMessage} successfully`);
            this.getOrders();
          }
        },
        (err) => {
          this.toastr.error(`Failed to ${actionType} order`);
        },
      );
  }

  deleteOrder(order: any): void {
    const modalRef = this.modalService.open(VendingOrderModalComponent, {
      centered: true,
      backdrop: "static",
    });
    modalRef.componentInstance.title = "Delete Order";
    modalRef.componentInstance.modalType = "delete";
    modalRef.componentInstance.order = order;

    modalRef.result
      .then((result) => {
        if (result) {
          this.apiCallingService
            .callAPI(
              {},
              "DELETE",
              `${ConfigApi.URLS.VENDING.DELETE_ORDER}/${order._id}`,
            )
            .subscribe((res) => {
              if (this.apiCallingService.handleSuccess(res)) {
                this.toastr.success("Order deleted successfully");
                this.getOrders();
              }
            });
        }
      })
      .catch(() => {});
  }

  getAvailableActions(order: any): string[] {
    if (order.deliveryType === "pickup") {
      switch (order.status?.toLowerCase()) {
        case "pending":
          return ["readyForPickUp", "cancel"];
        case "readytopickup":
          return ["complete", "cancel"];
        default:
          return [];
      }
    }

    if (order.deliveryType === "delivery") {
      switch (order.status?.toLowerCase()) {
        case "pending":
          return ["accept", "cancel"];
        case "accepted":
          return ["outForDelivery", "cancel"];
        case "outfordelivery":
          return ["complete", "cancel"];
        default:
          return [];
      }
    }

    return [];
  }
  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case "pending":
        return "badge-warning";
      case "accepted":
        return "badge-info";
      case "out_for_delivery":
        return "badge-primary";
      case "completed":
        return "badge-success";
      case "cancelled":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  }

  onSearchChange() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.page = 1;
      this.getOrders();
    }, 500);
  }

  onStatusChange() {
    if (this.statusDebounceTimer) {
      clearTimeout(this.statusDebounceTimer);
    }

    this.statusDebounceTimer = setTimeout(() => {
      this.page = 1;
      this.getOrders();
    }, 300);
  }

  onUserSearchChange() {
    if (this.userSearchDebounceTimer) {
      clearTimeout(this.userSearchDebounceTimer);
    }

    if (this.selectedUserId && this.userSearchQuery === this.selectedUserName) {
      this.isUserDropdownOpen = true;
      return;
    }

    this.userSearchDebounceTimer = setTimeout(() => {
      this.searchUsers(true);
    }, 500);
  }

  async searchUsers(shouldOpenDropdown: boolean = true) {
    this.loadingUsers = true;
    if (shouldOpenDropdown) {
      this.isUserDropdownOpen = true;
    }

    const user = this.globals.user || this.globals.getUser();
    const operator_id = user ? user._id : "";
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const url = `${ConfigApi.URLS.VENDING.SEARCH_USERS}?operator_id=${operator_id}&username=${this.userSearchQuery}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          time_zone: timeZone,
          user_id: operator_id,
        },
      });
      const res = await response.json();

      this.loadingUsers = false;
      if (res.success || res.status === "success" || Array.isArray(res.data)) {
        this.users = Array.isArray(res.data) ? res.data : [];
        if (shouldOpenDropdown) {
          this.isUserDropdownOpen = true;
        }
      } else {
        this.users = [];
      }
    } catch (err) {
      this.loadingUsers = false;
      this.users = [];
      console.error("User search error:", err);
    }
  }

  getUserDisplayName(user: any): string {
    if (!user) return "";
    return (
      user.name ||
      user.username ||
      user.userName ||
      user.email ||
      "Unknown User"
    );
  }

  selectUser(user: any) {
    this.selectedUserId = user._id;
    const name = this.getUserDisplayName(user);
    this.userSearchQuery = name;
    this.selectedUserName = name;
    this.isUserDropdownOpen = false;
    this.page = 1;
    this.getOrders();
  }

  exportCsv(): void {
    this.exportingCsv = true;
    const user = this.globals.user || this.globals.getUser();
    const user_id = user ? user._id : "";

    let params = new HttpParams().set("operator_id", user_id);

    if (this.searchQuery) {
      params = params.set("search", this.searchQuery);
    }
    if (this.selectedStatus && this.selectedStatus !== "all") {
      params = params.set("status", this.selectedStatus);
    }
    if (this.selectedUserId) {
      params = params.set("user_id", this.selectedUserId);
    }

    this.apiCallingService
      .callAPI(
        {},
        "GET",
        `${ConfigApi.URLS.VENDING.ORDER_LIST_EXPORT}?${params.toString()}`,
      )
      .subscribe(
        (res) => {
          this.exportingCsv = false;
          if (this.apiCallingService.handleSuccess(res)) {
            this.downloadCsv(res.data);
          }
        },
        () => {
          this.exportingCsv = false;
          this.toastr.error("Failed to export CSV");
        },
      );
  }

  private downloadCsv(data: any[]): void {
    // Order-level columns (15) + item-level columns (10)
    const headers = [
      "Order Number",
      "Invoice Number",
      "Status",
      "Delivery Type",
      "Store / Pickup Address",
      "Payment Method",
      "Subtotal (Without VAT)",
      "Total VAT Amount",
      "Delivery Charge",
      "Total Amount",
      "Order Date",
      "Customer Name",
      "Customer Email",
      "Customer Mobile",
      "Customer Address",
      // Item-level columns
      "Product Name",
      "Barcode ID (EAN)",
      "Quantity",
      "Unit Price",
      "Membership Discount",
      "Coupon Discount",
      "Amount (Without VAT)",
      "VAT %",
      "VAT Amount",
      "Product Total",
    ];

    const ORDER_COL_COUNT = 15;
    const ITEM_COL_COUNT = 10;
    const BLANK_ORDER_COLS = new Array(ORDER_COL_COUNT).fill("");
    const BLANK_ITEM_COLS = new Array(ITEM_COL_COUNT).fill("");

    const csvRows: string[][] = [headers];

    for (const order of data) {
      const cur = order.currency || "";

      const deliveryType = order.storeDeliveryServiceId
        ? "Shipping"
        : order.deliveryType === "delivery"
          ? "Home Delivery"
          : order.deliveryType || "";

      const storeAddress =
        order.deliveryType === "pickup"
          ? order.storeLocationId?.address ||
            order.operatorStoreId?.storeAddress ||
            ""
          : "-";

      const orderDate = order.created_at
        ? new Date(order.created_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "";

      // Order-level row — item columns left blank
      csvRows.push([
        order.orderNumber || "",
        order.invoice_number || "",
        order.status || "",
        deliveryType,
        storeAddress,
        order.paymentMethodType || "",
        `${cur} ${(order.totalAmountWithoutVat || 0).toFixed(2)}`,
        `${cur} ${(order.totalVatAmount || 0).toFixed(2)}`,
        `${cur} ${(order.deliveryPrice || 0).toFixed(2)}`,
        `${cur} ${((order.chargeableAmount || 0) / 100).toFixed(2)}`,
        orderDate,
        order.user_id?.name || "",
        order.user_id?.email || "",
        `${order.user_id?.dialling_code || ""} ${order.user_id?.mobile || ""}`.trim(),
        order.userAddress || "",
        ...BLANK_ITEM_COLS,
      ]);

      // Item-level rows — order columns left blank
      for (const item of order.vendingPackagesItems || []) {
        const pkg = item.packageId || {};
        const membershipDiscount =
          item.membershipDiscountPercentage > 0
            ? `${item.membershipDiscountPercentage}% (-${cur} ${(item.totalMembershipDiscount || 0).toFixed(2)})`
            : "N/A";
        const couponDiscount =
          item.discountAmount > 0
            ? `-${cur} ${(item.discountAmount || 0).toFixed(2)}`
            : "N/A";

        csvRows.push([
          ...BLANK_ORDER_COLS,
          pkg.name || "",
          pkg.EANNumber || "",
          String(item.quantity || 0),
          `${cur} ${(pkg.price || 0).toFixed(2)}`,
          membershipDiscount,
          couponDiscount,
          `${cur} ${(item.totalAmountWithoutVat || 0).toFixed(2)}`,
          `${item.totalVatPercentage || 0}%`,
          `${cur} ${(item.totalVatAmount || 0).toFixed(2)}`,
          `${cur} ${(item.chargeableAmount || 0).toFixed(2)}`,
        ]);
      }

      // Empty separator row between orders
      csvRows.push(new Array(ORDER_COL_COUNT + ITEM_COL_COUNT).fill(""));
    }

    const csvContent = csvRows
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `orders_export_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  clearUserFilter() {
    this.selectedUserId = "";
    this.selectedUserName = "";
    this.userSearchQuery = "";
    this.isUserDropdownOpen = false;
    this.page = 1;
    this.getOrders();
  }
}
